import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import './AdminCollegeList.css';

if (typeof document !== 'undefined') {
  Modal.setAppElement('#root');
}

const ITEMS_PER_PAGE = 10;

const AdminCollegeList = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [filterCountry, setFilterCountry] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [studentModalIsOpen, setStudentModalIsOpen] = useState(false);
  const [selectedCollegeName, setSelectedCollegeName] = useState('');
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);

  const [profileModalIsOpen, setProfileModalIsOpen] = useState(false);
  const [selectedCollegeProfile, setSelectedCollegeProfile] = useState(null);

  useEffect(() => {
    fetchColleges();
    // eslint-disable-next-line
  }, []);

  // Reset to page 1 whenever search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCountry]);

  const fetchColleges = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        alert('Session expired. Please login again.');
        navigate('/');
        return;
      }
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/colleges`,
        { headers: { Authorization: token } }
      );
      setColleges(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching colleges', error);
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        navigate('/');
      }
      setLoading(false);
    }
  };

  const handleViewStudents = async (collegeName) => {
    setProfileModalIsOpen(false);
    setSelectedCollegeProfile(null);
    setSelectedCollegeName(collegeName);
    setStudentModalIsOpen(true);
    setStudentsLoading(true);
    setStudents([]);
    try {
      const token = localStorage.getItem('adminToken');
      const encodedName = encodeURIComponent(collegeName);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/colleges/students/${encodedName}`,
        { headers: { Authorization: token } }
      );
      setStudents(res.data);
    } catch (error) {
      console.error('Error fetching students', error);
      if (error.response && error.response.status === 401) {
        alert('Session expired. Please login again.');
        navigate('/');
      } else {
        alert('Failed to fetch student details.');
      }
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleViewProfile = (college) => {
    setStudentModalIsOpen(false);
    setStudents([]);
    setStudentsLoading(false);
    setSelectedCollegeProfile(college);
    setProfileModalIsOpen(true);
  };

  const closeStudentModal = () => {
    setStudentModalIsOpen(false);
    setStudents([]);
    setStudentsLoading(false);
  };

  const closeProfileModal = () => {
    setProfileModalIsOpen(false);
    setSelectedCollegeProfile(null);
  };

  // ── Filter logic ──
  const filteredColleges = colleges.filter((college) => {
    const matchesCountry =
      filterCountry === 'All'
        ? true
        : college.country?.toLowerCase() === filterCountry.toLowerCase();
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      (college.collegeName && college.collegeName.toLowerCase().includes(term)) ||
      (college.collegeRegNo && college.collegeRegNo.toLowerCase().includes(term)) ||
      (college.city && college.city.toLowerCase().includes(term));
    return matchesCountry && matchesSearch;
  });

  // ── Pagination logic ──
  const totalPages = Math.ceil(filteredColleges.length / ITEMS_PER_PAGE);
  const paginatedColleges = filteredColleges.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Build page number array with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  const uniqueCountries = [
    ...new Set(colleges.map((c) => c.country).filter(Boolean)),
  ].sort();

  return (
    <div className="acl-wrapper">

      {/* ── PAGE HEADER ── */}
      <div className="acl-page-header">
        <div className="acl-page-header-left">
          <div className="acl-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <div>
            <h1 className="acl-page-title">Registered Colleges</h1>
            <p className="acl-page-subtitle">
              {filteredColleges.length} college{filteredColleges.length !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        <div className="acl-filters-row">
          {/* Search */}
          <div className="acl-search-box">
            <svg className="acl-search-icon" width="15" height="15" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, reg no or city…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="acl-search-input"
            />
            {searchTerm && (
              <button className="acl-search-clear" onClick={() => setSearchTerm('')}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {/* Country select */}
          <div className="acl-select-box">
            <svg className="acl-select-icon" width="15" height="15" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <select
              className="acl-country-select"
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
            >
              <option value="All">All Countries</option>
              {uniqueCountries.map((country, i) => (
                <option key={i} value={country}>{country}</option>
              ))}
            </select>
            <svg className="acl-select-chevron" width="13" height="13" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>
      </div>

      {/* ── TABLE CARD ── */}
      <div className="acl-card">
        {loading ? (
          <div className="acl-loading-state">
            <div className="acl-spinner" />
            <p>Loading colleges…</p>
          </div>
        ) : (
          <>
            <div className="acl-table-scroll">
              <table className="acl-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Reg No</th>
                    <th>College Name</th>
                    <th>Country</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Phone</th>
                    <th>Placement Officer</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedColleges.length > 0 ? (
                    paginatedColleges.map((college, index) => (
                      <tr key={college._id}>
                        <td className="acl-serial">
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </td>
                        <td>
                          <span className="acl-reg-badge">{college.collegeRegNo}</span>
                        </td>
                        <td className="acl-college-name">{college.collegeName}</td>
                        <td>{college.country || '—'}</td>
                        <td>{college.city || '—'}</td>
                        <td>{college.state || '—'}</td>
                        <td>{college.collegePhone || '—'}</td>
                        <td>{college.poName || '—'}</td>
                        <td>
                          <div className="acl-actions">
                            <button
                              className="acl-btn acl-btn-outline"
                              onClick={() => handleViewProfile(college)}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              Profile
                            </button>
                            <button
                              className="acl-btn acl-btn-gold"
                              onClick={() => handleViewStudents(college.collegeName)}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                              </svg>
                              Students
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="acl-empty-row">
                        <div className="acl-empty-state">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="1.5"
                            strokeLinecap="round" strokeLinejoin="round"
                            style={{ opacity: 0.3, marginBottom: 10 }}>
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                          <p>No colleges match your search or filter.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── PAGINATION ── */}
            {totalPages > 1 && (
              <div className="acl-pagination">
                <span className="acl-page-info">
                  Showing{' '}
                  <strong>{(currentPage - 1) * ITEMS_PER_PAGE + 1}</strong>
                  {' – '}
                  <strong>
                    {Math.min(currentPage * ITEMS_PER_PAGE, filteredColleges.length)}
                  </strong>
                  {' of '}
                  <strong>{filteredColleges.length}</strong>
                </span>

                <div className="acl-page-controls">
                  {/* Prev */}
                  <button
                    className="acl-page-btn acl-page-arrow"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6" />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {getPageNumbers().map((page, i) =>
                    page === '...' ? (
                      <span key={`ellipsis-${i}`} className="acl-page-ellipsis">…</span>
                    ) : (
                      <button
                        key={page}
                        className={`acl-page-btn ${currentPage === page ? 'acl-page-active' : ''}`}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    )
                  )}

                  {/* Next */}
                  <button
                    className="acl-page-btn acl-page-arrow"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ════════════════════════════════════
          MODAL 1 — COLLEGE PROFILE
      ════════════════════════════════════ */}
      <Modal
        isOpen={profileModalIsOpen}
        onRequestClose={closeProfileModal}
        contentLabel="College Profile"
        className="acl-modal"
        overlayClassName="acl-overlay"
        shouldCloseOnOverlayClick
      >
        {selectedCollegeProfile && (
          <>
            <div className="acl-modal-header">
              <div className="acl-modal-title-group">
                <div className="acl-modal-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </div>
                <div>
                  <h2 className="acl-modal-title">College Profile</h2>
                  <p className="acl-modal-subtitle">{selectedCollegeProfile.collegeName}</p>
                </div>
              </div>
              <button className="acl-close-x" onClick={closeProfileModal} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="acl-modal-body">
              {selectedCollegeProfile.profilePicture && (
                <div className="acl-avatar-row">
                  <img
                    src={selectedCollegeProfile.profilePicture}
                    alt="College Logo"
                    className="acl-avatar"
                  />
                </div>
              )}

              <div className="acl-section-label">Basic Information</div>
              <div className="acl-info-grid">
                <div className="acl-info-item">
                  <span className="acl-info-key">College Name</span>
                  <span className="acl-info-val">{selectedCollegeProfile.collegeName || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">Registration No.</span>
                  <span className="acl-info-val acl-mono">{selectedCollegeProfile.collegeRegNo || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">System ID</span>
                  <span className="acl-info-val acl-mono">{selectedCollegeProfile.systemId || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">University</span>
                  <span className="acl-info-val">{selectedCollegeProfile.universityName || '—'}</span>
                </div>
              </div>

              <div className="acl-section-label">Address</div>
              <div className="acl-info-grid">
                <div className="acl-info-item acl-info-full">
                  <span className="acl-info-key">Street Address</span>
                  <span className="acl-info-val">{selectedCollegeProfile.streetAddress || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">City</span>
                  <span className="acl-info-val">{selectedCollegeProfile.city || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">State</span>
                  <span className="acl-info-val">{selectedCollegeProfile.state || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">Country</span>
                  <span className="acl-info-val">{selectedCollegeProfile.country || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">Pincode</span>
                  <span className="acl-info-val acl-mono">{selectedCollegeProfile.pincode || '—'}</span>
                </div>
              </div>

              <div className="acl-section-label">Contact Details</div>
              <div className="acl-info-grid">
                <div className="acl-info-item">
                  <span className="acl-info-key">Phone</span>
                  <span className="acl-info-val">{selectedCollegeProfile.collegePhone || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">Email</span>
                  <span className="acl-info-val">{selectedCollegeProfile.collegeEmail || '—'}</span>
                </div>
              </div>

              <div className="acl-section-label">Placement Officer</div>
              <div className="acl-info-grid">
                <div className="acl-info-item">
                  <span className="acl-info-key">Name</span>
                  <span className="acl-info-val">{selectedCollegeProfile.poName || '—'}</span>
                </div>
                <div className="acl-info-item">
                  <span className="acl-info-key">Phone</span>
                  <span className="acl-info-val">{selectedCollegeProfile.poPhone || '—'}</span>
                </div>
                <div className="acl-info-item acl-info-full">
                  <span className="acl-info-key">Email</span>
                  <span className="acl-info-val">{selectedCollegeProfile.poEmail || '—'}</span>
                </div>
              </div>
            </div>

            <div className="acl-modal-footer">
              <button className="acl-btn acl-btn-gold" onClick={closeProfileModal}>
                Close
              </button>
            </div>
          </>
        )}
      </Modal>

      {/* ════════════════════════════════════
          MODAL 2 — STUDENTS LIST
      ════════════════════════════════════ */}
      <Modal
        isOpen={studentModalIsOpen}
        onRequestClose={closeStudentModal}
        contentLabel="Students List"
        className="acl-modal acl-modal-wide"
        overlayClassName="acl-overlay"
        shouldCloseOnOverlayClick
      >
        <div className="acl-modal-header">
          <div className="acl-modal-title-group">
            <div className="acl-modal-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div>
              <h2 className="acl-modal-title">Enrolled Students</h2>
              <p className="acl-modal-subtitle">{selectedCollegeName}</p>
            </div>
          </div>
          <button className="acl-close-x" onClick={closeStudentModal} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="acl-modal-body acl-modal-body-students">
          {studentsLoading ? (
            <div className="acl-loading-state">
              <div className="acl-spinner" />
              <p>Loading students…</p>
            </div>
          ) : students.length > 0 ? (
            <>
              <p className="acl-student-count">
                {students.length} student{students.length !== 1 ? 's' : ''} enrolled
              </p>
              <div className="acl-student-table-scroll">
                <table className="acl-student-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Roll No</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Stream</th>
                      <th>CGPA</th>
                      <th>Expected CTC</th>
                      <th>Resume</th>
                      <th>LinkedIn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, idx) => (
                      <tr key={student._id}>
                        <td className="acl-serial">{idx + 1}</td>
                        <td>
                          <span className="acl-roll-badge">{student.rollNo}</span>
                        </td>
                        <td className="acl-student-name">{student.fullName}</td>
                        <td className="acl-email-cell">{student.personalEmail}</td>
                        <td>{student.phoneNumber || '—'}</td>
                        <td>{student.twelfthStream || '—'}</td>
                        <td className="acl-cgpa-cell">
                          {student.currentCGPA ? (
                            <span className="acl-cgpa-badge">{student.currentCGPA}</span>
                          ) : '—'}
                        </td>
                        <td>{student.expectedCTC || '—'}</td>
                        <td>
                          {student.resumeFile ? (
                            <a href={student.resumeFile} target="_blank"
                              rel="noreferrer" className="acl-resume-btn">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                              Download
                            </a>
                          ) : (
                            <span className="acl-na">N/A</span>
                          )}
                        </td>
                        <td>
                          {student.linkedinURL ? (
                            <a href={student.linkedinURL} target="_blank"
                              rel="noreferrer" className="acl-linkedin-btn">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5"
                                strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                                <rect x="2" y="9" width="4" height="12" />
                                <circle cx="4" cy="4" r="2" />
                              </svg>
                              View
                            </a>
                          ) : (
                            <span className="acl-na">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="acl-empty-state">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ opacity: 0.3, marginBottom: 12 }}>
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
              <p>No students found under this college.</p>
            </div>
          )}
        </div>

        <div className="acl-modal-footer">
          <button className="acl-btn acl-btn-gold" onClick={closeStudentModal}>
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminCollegeList;